import React, { useCallback, useRef, useState } from "react";
import { MAX_CV_HEADLINE, MAX_CV_DESCRIPTION } from "@tabbio/utils";
import { EXPERIENCE_LOCATION_TYPES } from "@tabbio/utils/experience-location-type";
import { useToast } from "../../../hooks/useToast";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { Briefcase, Plus, X } from "lucide-react";
import { useCVData, type CVExperience } from "../../../lib/cv-mock-data";
import {
  useAIDraftExperience,
  useAIEnhanceText,
} from "../../../hooks/useOnboardingApi";
import {
  useSparkEnhance,
  SparkEnhanceEntitlementError,
} from "../../../hooks/useSparkEnhance";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import {
  buildAiDraftProfileContext,
  mapAIDraftExperienceToCVExperience,
} from "../../../lib/onboarding-ai-drafts";
import {
  WebInput,
  WebTextarea,
  WebSaveButton,
  WebFormDialog,
  WebItemCard,
  WebEmptyState,
  WebToggle,
  SparkButton,
  SparkPreview,
  type SparkScope,
} from "./web-form-elements";
import { CvListToolbar } from "./CvListToolbar";
import { ReorderableCvList } from "./ReorderableCvList";
import { WebDatePicker, WebLocationPicker } from "./web-pickers";
import { DescribeToAIField } from "./DescribeToAIField";
import {
  ExperienceErrors,
  composePeriod,
  currentRoleStartDateError,
  hasAnyExperienceContent,
  normalizeExperiencePeriods,
  parsePeriodParts,
  validateExperience,
} from "./experience-validation";
import { useHydratedArrayState } from "./useHydratedFormState";
import { confirmDeleteCvItem } from "./delete-confirmation";
import { useExperienceReviewQueue } from "./useExperienceReviewQueue";
import { cvChangeNotApplied } from "../../../lib/cv-write-coordinator";

const ERROR_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: "var(--tabbio-danger, #e53e3e)",
  marginTop: 4,
  display: "block",
};

// Issue #36: visible character counter so the field cap is no longer a silent
// limit. Overview shows it always; short name fields only once within ~10% of
// the cap. A subtle inline message appears when the cap is reached.
const NEAR_LIMIT_RATIO = 0.9;
function WebCharCounter({
  value,
  max,
  alwaysShow,
}: {
  value: string;
  max: number;
  alwaysShow?: boolean;
}) {
  const { t } = useTranslation("seeker");
  const len = value.length;
  const near = len >= Math.floor(max * NEAR_LIMIT_RATIO);
  if (!alwaysShow && !near) return null;
  const atCap = len >= max;
  const color = atCap
    ? "var(--tabbio-danger, #e53e3e)"
    : "var(--text-tertiary)";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        marginTop: 4,
        paddingInline: 4,
      }}
    >
      <span style={{ fontSize: 11, color: "var(--tabbio-danger, #e53e3e)" }}>
        {atCap ? t("cv.edit.limitReached") : ""}
      </span>
      <span
        style={{ fontSize: 11, color, fontVariantNumeric: "tabular-nums" }}
      >{`${len} / ${max}`}</span>
    </div>
  );
}

interface EditExperienceFormProps {
  onSave?: () => Promise<void> | void;
  closeOnSave?: boolean;
  ctaLabel?: string;
  /** Hide skill application when no persisted CV target exists yet. */
  sparkApplySkill?: boolean;
}
function cloneExperience(item: CVExperience): CVExperience {
  return { ...item, bullets: [...item.bullets] };
}

export function EditExperienceForm({
  onSave,
  closeOnSave = true,
  ctaLabel,
  sparkApplySkill = true,
}: EditExperienceFormProps) {
  const { t } = useTranslation("seeker");
  const { profile, updateProfile } = useCVData();
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const draftExperienceMutation = useAIDraftExperience();
  const enhanceTextMutation = useAIEnhanceText();
  const spark = useSparkEnhance();
  const syncExperienceToProfile = useCallback(
    (nextItems: CVExperience[]) => {
      updateProfile({
        experience: nextItems.filter(hasAnyExperienceContent),
      });
    },
    [updateProfile],
  );

  const [items, setItems] = useHydratedArrayState(
    profile.experience,
    cloneExperience,
    syncExperienceToProfile,
  );
  const [describeText, setDescribeText] = useState("");
  // Spark enhance state for the dialog: overview description + per-bullet achievement.
  const [pendingOverview, setPendingOverview] = useState<string | null>(null);
  const [pendingBullet, setPendingBullet] = useState<{
    index: number;
    text: string;
  } | null>(null);
  const [enhancingTarget, setEnhancingTarget] = useState<
    "overview" | number | null
  >(null);
  const resetSpark = () => {
    setPendingOverview(null);
    setPendingBullet(null);
  };
  // Issue #134: Enter-only submission had no visible affordance. Lift the
  // input to controlled state so an explicit Add button can submit it too.
  const [bulletInput, setBulletInput] = useState("");
  const [bulletAddHover, setBulletAddHover] = useState(false);
  const [currentStartError, setCurrentStartError] = useState("");
  const bulletInputRef = useRef<HTMLInputElement>(null);
  const review = useExperienceReviewQueue({
    t,
    setItems,
    labels: {
      addTitle: t("cv.edit.addPosition"),
      editTitle: t("cv.edit.experience"),
    },
    onModalReset: () => {
      setBulletInput("");
      resetSpark();
    },
  });
  const {
    editingIndex,
    draft,
    setDraft,
    showErrors,
    setShowErrors,
    errors,
    hasErrors,
  } = review;
  const addBulletFromInput = () => {
    const val = bulletInput.trim();
    if (!val) return;
    setDraft({ ...draft, bullets: [...draft.bullets, val] });
    setBulletInput("");
    setPendingBullet(null);
    // Issue #295: the new row is inserted above this field, so on Expo web with
    // a soft keyboard it slides out of the visible area. Keep focus (the
    // keyboard stays up) and pull the field back into view after the row lays
    // out, so the next achievement can be typed straight away.
    requestAnimationFrame(() => {
      bulletInputRef.current?.focus();
      bulletInputRef.current?.scrollIntoView({ block: "nearest" });
    });
  };
  const updateBullet = (index: number, text: string) => {
    setDraft({
      ...draft,
      bullets: draft.bullets.map((bullet, bulletIndex) =>
        bulletIndex === index ? text : bullet,
      ),
    });
    if (pendingBullet?.index === index) setPendingBullet(null);
  };
  const removeBullet = (index: number) => {
    setDraft({
      ...draft,
      bullets: draft.bullets.filter((_, bulletIndex) => bulletIndex !== index),
    });
    setPendingBullet(null);
  };
  const confirmRemoveBullet = (index: number) => {
    void confirmDeleteCvItem(t, 'achievement', () => removeBullet(index));
  };

  const enhanceContext = () => ({
    title: draft.role || profile.headline,
    company: draft.company || undefined,
  });

  const enhanceOverview = async (scope: SparkScope = "section") => {
    const text = draft.overview.trim();
    if (!text || enhancingTarget !== null) return;
    setEnhancingTarget("overview");
    try {
      await spark.ensureAllowed();
      try {
        const result = await enhanceTextMutation.mutateAsync({
          text: draft.overview,
          field: "experience_description",
          scope,
          context: enhanceContext(),
        });
        if (result.enhanced) setPendingOverview(result.enhanced);
        else
          toast.error(
            "Generation failed",
            "AI returned an empty result. Please try again.",
          );
      } finally {
        await spark.reconcile();
      }
    } catch (err) {
      if (err instanceof SparkEnhanceEntitlementError) return; // upgrade modal opened
      toast.error(
        "Generation failed",
        err instanceof Error ? err.message : "Could not enhance description",
      );
    } finally {
      setEnhancingTarget(null);
    }
  };

  const enhanceBullet = async (
    index: number,
    scope: SparkScope = "section",
  ) => {
    const text = (draft.bullets[index] ?? "").trim();
    if (!text || enhancingTarget !== null) return;
    setEnhancingTarget(index);
    try {
      await spark.ensureAllowed();
      try {
        const result = await enhanceTextMutation.mutateAsync({
          text: draft.bullets[index],
          field: "experience_achievement",
          scope,
          context: enhanceContext(),
        });
        if (result.enhanced) setPendingBullet({ index, text: result.enhanced });
        else
          toast.error(
            "Generation failed",
            "AI returned an empty result. Please try again.",
          );
      } finally {
        await spark.reconcile();
      }
    } catch (err) {
      if (err instanceof SparkEnhanceEntitlementError) return; // upgrade modal opened
      toast.error(
        "Generation failed",
        err instanceof Error ? err.message : "Could not enhance achievement",
      );
    } finally {
      setEnhancingTarget(null);
    }
  };
  // Issue #32: stash the last real end date so toggling "I work here" off can
  // restore it (the period string can't hold an end while current=true). Reset
  // when the edited item changes so one entry's end can't leak into another.
  const lastEndRef = useRef("");
  const lastEndIdRef = useRef<string | null>(null);
  const handleDescribe = async () => {
    const text = describeText.trim();
    if (!text || draftExperienceMutation.isPending) return;
    try {
      const result = await draftExperienceMutation.mutateAsync({
        prompt: text,
        profile: buildAiDraftProfileContext(profile),
      });
      const parsed = result.experiences ?? [];
      if (parsed.length === 0) {
        toast.error("Generation failed", "Could not draft experience");
        return;
      }
      const drafted = parsed.map((experience) =>
        mapAIDraftExperienceToCVExperience(experience, text),
      );
      review.beginReview(drafted);
      setDescribeText("");
    } catch (err) {
      toast.error(
        "Generation failed",
        err instanceof Error ? err.message : "Could not draft experience",
      );
    }
  };
  const visibleErrors: ExperienceErrors = showErrors ? errors : {};

  const openEdit = (i: number) => {
    setCurrentStartError("");
    review.openEdit(items[i], i);
  };
  const openAdd = () => {
    setCurrentStartError("");
    review.openAdd();
  };
  const handleDialogSave = review.commit;

  const handleCardDelete = (i: number) => {
    void confirmDeleteCvItem(t, 'position', () =>
      setItems(items.filter((_, idx) => idx !== i)),
    );
  };

  const handleSave = async () => {
    if (isSaving || draftExperienceMutation.isPending) return;
    // Repair imported period text FIRST. A CV whose dates read "2019 to 2021"
    // or "Mar-2015" is not invalid data: it is the picker's own value in a
    // different notation, and normalizing it here means those entries never
    // reach the gate below.
    const normalizedItems = normalizeExperiencePeriods(items);
    if (normalizedItems !== items) setItems(normalizedItems);
    const persistable = normalizedItems.filter(hasAnyExperienceContent);
    // Imported CVs can land with incomplete entries (e.g. no role title). A
    // generic failure toast dead-ends the user: open the first invalid entry
    // with its field errors visible so the fix is one obvious step away.
    const firstInvalidIndex = normalizedItems.findIndex(
      (item) =>
        hasAnyExperienceContent(item) &&
        Object.keys(validateExperience(item, t)).length > 0,
    );
    if (firstInvalidIndex >= 0) {
      review.openEdit(normalizedItems[firstInvalidIndex], firstInvalidIndex);
      setShowErrors(true);
      toast.error(
        t("cv.edit.saveFailed"),
        t("cv.edit.completePosition", {
          defaultValue:
            "Some details are missing: complete the highlighted position.",
        }),
      );
      return;
    }
    updateProfile({ experience: persistable });
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave();
        toast.success(t("cv.edit.saved"));
        if (closeOnSave && router.canGoBack()) router.back();
      } catch (error) {
        // B02: a CV conflict the user answered is not a failure. "Discard"
        // keeps the version saved elsewhere; "keep editing" keeps this draft.
        const outcome = cvChangeNotApplied(error);
        if (outcome) {
          if (outcome === "discarded" && closeOnSave && router.canGoBack()) router.back();
          return;
        }
        toast.error(t("cv.edit.saveFailed"));
      } finally {
        setIsSaving(false);
      }
    } else {
      router.back();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <CvListToolbar
        addLabel={t("cv.edit.addPosition")}
        onAdd={openAdd}
        showReorderHint={items.length > 1}
      />
      <ReorderableCvList
        items={items}
        onReorder={setItems}
        keyExtractor={(item) => item.id}
        getItemLabel={(item) =>
          item.role || item.company || t("cv.edit.experience")
        }
        renderItem={({ item, index }) => (
          <WebItemCard
            title={item.role || item.company}
            subtitle={[
              item.role ? item.company : "",
              item.locationType,
              item.period,
            ]
              .filter(Boolean)
              .join(" · ")}
            icon={
              <Briefcase
                size={18}
                color="var(--text-secondary)"
                strokeWidth={1.75}
              />
            }
            onClick={() => openEdit(index)}
            onDelete={() => handleCardDelete(index)}
          />
        )}
        emptyState={
          <WebEmptyState
            icon={
              <Briefcase
                size={32}
                color="var(--text-tertiary)"
                strokeWidth={1.25}
              />
            }
            text={t("cv.edit.noExperience", {
              defaultValue: "No experience added yet",
            })}
          />
        }
      />
      <DescribeToAIField
        value={describeText}
        onChangeText={setDescribeText}
        onSubmit={handleDescribe}
        loading={draftExperienceMutation.isPending}
        disabled={isSaving}
      />
      <WebSaveButton
        label={ctaLabel ?? t("cv.edit.save")}
        onClick={handleSave}
        loading={isSaving}
        disabled={isSaving || draftExperienceMutation.isPending}
      />

      <WebFormDialog
        visible={editingIndex !== null}
        title={review.modalTitle}
        onClose={review.close}
        footer={
          <button
            onClick={handleDialogSave}
            disabled={showErrors && hasErrors}
            style={{
              width: "100%",
              height: 48,
              borderRadius: 9999,
              backgroundColor: "var(--text-primary)",
              color: "var(--bg)",
              fontSize: 16,
              fontWeight: 600,
              border: "none",
              cursor: showErrors && hasErrors ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              opacity: showErrors && hasErrors ? 0.5 : 1,
            }}
          >
            {editingIndex === -1 ? t("cv.edit.addPosition") : t("cv.edit.save")}
          </button>
        }
      >
        <WebInput
          label={t("cv.edit.role")}
          value={draft.role}
          onChange={(v) => setDraft({ ...draft, role: v })}
          placeholder={t("cv.edit.role")}
        />
        {visibleErrors.role ? (
          <span style={ERROR_STYLE}>{visibleErrors.role}</span>
        ) : null}
        <WebCharCounter value={draft.role} max={MAX_CV_HEADLINE} />
        <WebInput
          label={t("cv.edit.company")}
          value={draft.company}
          onChange={(v) => setDraft({ ...draft, company: v })}
          placeholder={t("cv.edit.company")}
        />
        {visibleErrors.company ? (
          <span style={ERROR_STYLE}>{visibleErrors.company}</span>
        ) : null}
        <WebCharCounter value={draft.company} max={MAX_CV_HEADLINE} />
        <div style={{ marginTop: 12 }}>
          <WebLocationPicker
            label={t("cv.edit.location")}
            value={draft.location}
            onSelect={(v) => setDraft({ ...draft, location: v })}
            placeholder={t("cv.edit.location")}
            includeRemote={false}
          />
        </div>
        {visibleErrors.location ? (
          <span style={ERROR_STYLE}>{visibleErrors.location}</span>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <label
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--text-secondary)",
              marginBottom: 8,
              display: "block",
            }}
          >
            {t("cv.edit.locationType")}
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {EXPERIENCE_LOCATION_TYPES.map((locationType) => {
              const selected = draft.locationType === locationType;
              const labelKey =
                locationType === "Remote"
                  ? "cv.edit.locationTypeRemote"
                  : locationType === "Hybrid"
                    ? "cv.edit.locationTypeHybrid"
                    : "cv.edit.locationTypeOnSite";
              return (
                <button
                  key={locationType}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setDraft({ ...draft, locationType })}
                  style={{
                    height: 34,
                    paddingInline: 16,
                    borderRadius: 9999,
                    border: "none",
                    backgroundColor: selected
                      ? "var(--color-discovery, #5e29ff)"
                      : "var(--surface-secondary, var(--tabbio-surface-secondary, rgba(0,0,0,0.04)))",
                    color: selected ? "#ffffff" : "var(--text-primary)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 500,
                    transition: "background-color 150ms, color 150ms",
                  }}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>
        {(() => {
          const { start, end, current } = parsePeriodParts(draft.period);
          if (lastEndIdRef.current !== draft.id) {
            lastEndIdRef.current = draft.id;
            lastEndRef.current = end;
          }
          if (end) lastEndRef.current = end;
          const setStart = (v: string) => {
            setCurrentStartError("");
            setDraft({ ...draft, period: composePeriod(v, end, current) });
          };
          const setEnd = (v: string) => {
            lastEndRef.current = v;
            setDraft({ ...draft, period: composePeriod(start, v, current) });
          };
          const setCurrent = (v: boolean) => {
            const startError = v ? currentRoleStartDateError(start, t) : undefined;
            if (startError) {
              setCurrentStartError(startError);
              return;
            }
            setCurrentStartError("");
            setDraft({
              ...draft,
              period: composePeriod(start, v ? "" : lastEndRef.current, v),
            });
          };
          return (
            <>
              <div style={{ marginTop: 12 }}>
                <WebDatePicker
                  label={t("cv.edit.startDate")}
                  value={start}
                  onSelect={setStart}
                  placeholder={t("cv.edit.startDate")}
                />
              </div>
              {visibleErrors.start || currentStartError ? (
                <span style={ERROR_STYLE}>{visibleErrors.start || currentStartError}</span>
              ) : null}
              {!current ? (
                <>
                  <div style={{ marginTop: 12 }}>
                    <WebDatePicker
                      label={t("cv.edit.endDate")}
                      value={end}
                      onSelect={setEnd}
                      placeholder={t("cv.edit.endDate")}
                    />
                  </div>
                  {visibleErrors.end ? (
                    <span style={ERROR_STYLE}>{visibleErrors.end}</span>
                  ) : null}
                </>
              ) : null}
              <div style={{ marginTop: 12 }}>
                <WebToggle
                  label={t("cv.edit.currentRole")}
                  value={current}
                  onChange={setCurrent}
                />
              </div>
            </>
          );
        })()}
        <WebTextarea
          label={t("cv.edit.overview")}
          value={draft.overview}
          onChange={(v) => {
            setDraft({ ...draft, overview: v });
            if (pendingOverview) setPendingOverview(null);
          }}
          placeholder={t("cv.edit.overview")}
          rows={3}
        />
        {visibleErrors.overview ? (
          <span style={ERROR_STYLE}>{visibleErrors.overview}</span>
        ) : null}
        <WebCharCounter
          value={draft.overview}
          max={MAX_CV_DESCRIPTION}
          alwaysShow
        />
        {draft.overview.trim() ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 4,
            }}
          >
            <SparkButton
              onEnhance={enhanceOverview}
              loading={enhancingTarget === "overview"}
              disabled={enhancingTarget !== null}
              showApplySkill={sparkApplySkill}
            />
          </div>
        ) : null}
        {pendingOverview ? (
          <SparkPreview
            text={pendingOverview}
            onAccept={() => {
              setDraft({ ...draft, overview: pendingOverview });
              setPendingOverview(null);
            }}
            onReject={() => setPendingOverview(null)}
          />
        ) : null}
        <div style={{ marginTop: 8 }}>
          <label
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--text-secondary)",
              marginBottom: 6,
              display: "block",
            }}
          >
            Achievements
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {draft.bullets.map((b, bi) => (
              <div
                key={bi}
                style={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 12px",
                    border: "1px solid var(--input-border)",
                    borderRadius: 16,
                    backgroundColor: "var(--input-bg)",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "var(--text-primary)",
                      flexShrink: 0,
                      marginTop: 10,
                    }}
                  />
                  <textarea
                    value={b}
                    onChange={(event) => updateBullet(bi, event.target.value)}
                    placeholder={t("cv.edit.addBullet")}
                    rows={2}
                    maxLength={MAX_CV_DESCRIPTION}
                    dir="auto"
                    style={{
                      flex: 1,
                      minHeight: 44,
                      resize: "vertical",
                      border: "none",
                      outline: "none",
                      padding: 0,
                      backgroundColor: "transparent",
                      color: "var(--text-primary)",
                      fontSize: 14,
                      lineHeight: "20px",
                      fontFamily: "inherit",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => enhanceBullet(bi)}
                      disabled={enhancingTarget !== null || !b.trim()}
                      aria-label={t("cv.edit.spark.enhance", {
                        defaultValue: "Enhance",
                      })}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        background: "transparent",
                        border: "none",
                        cursor:
                          enhancingTarget !== null || !b.trim()
                            ? "default"
                            : "pointer",
                        padding: 0,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: enhancingTarget === bi ? 0.5 : 1,
                      }}
                    >
                      <MaterialSymbol
                        name="auto_awesome"
                        size={15}
                        style={{ color: "var(--text-secondary)" }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmRemoveBullet(bi)}
                      aria-label={t("cv.edit.remove", {
                        defaultValue: "Remove",
                      })}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <X
                        size={14}
                        color="var(--tabbio-danger, #e53e3e)"
                        strokeWidth={2}
                      />
                    </button>
                  </div>
                </div>
                {pendingBullet && pendingBullet.index === bi ? (
                  <SparkPreview
                    text={pendingBullet.text}
                    onAccept={() => {
                      const next = [...draft.bullets];
                      next[bi] = pendingBullet.text;
                      setDraft({ ...draft, bullets: next });
                      setPendingBullet(null);
                    }}
                    onReject={() => setPendingBullet(null)}
                  />
                ) : null}
                {/* Issue #173: counter so an over-long achievement is not silently capped. */}
                <WebCharCounter value={b} max={MAX_CV_DESCRIPTION} />
              </div>
            ))}
            {draft.bullets.length === 0 ? (
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-tertiary)",
                  paddingBlock: 4,
                }}
              >
                {t("cv.edit.addAchievementHint", {
                  defaultValue: "Type an achievement and press Add or Enter",
                })}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <input
                ref={bulletInputRef}
                type="text"
                value={bulletInput}
                onChange={(e) => setBulletInput(e.target.value)}
                placeholder={t("cv.edit.addBullet")}
                maxLength={MAX_CV_DESCRIPTION}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addBulletFromInput();
                  }
                }}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  fontSize: 14,
                  borderRadius: 9999,
                  border: "1px solid var(--input-border)",
                  backgroundColor: "var(--bg)",
                  color: "var(--text-primary)",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              <button
                type="button"
                onClick={addBulletFromInput}
                // Issue #295: pressing the button would otherwise blur the
                // input, which closes the soft keyboard on mobile web.
                // pointerdown covers touch as well as mouse, and suppressing its
                // default only cancels the focus-stealing mousedown, the click
                // that runs onClick is still dispatched.
                onPointerDown={(e) => e.preventDefault()}
                disabled={!bulletInput.trim()}
                onMouseEnter={() => setBulletAddHover(true)}
                onMouseLeave={() => setBulletAddHover(false)}
                aria-label={t("cv.edit.add")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  height: 40,
                  paddingInline: 16,
                  borderRadius: 9999,
                  border: "none",
                  cursor: bulletInput.trim() ? "pointer" : "not-allowed",
                  backgroundColor: "var(--text-primary)",
                  color: "var(--bg)",
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  flexShrink: 0,
                  opacity: bulletInput.trim() ? 1 : 0.5,
                  filter:
                    bulletAddHover && bulletInput.trim()
                      ? "brightness(1.15)"
                      : "brightness(1)",
                  transition: "filter 150ms, opacity 150ms",
                }}
              >
                <Plus size={16} strokeWidth={2} />
                {t("cv.edit.add")}
              </button>
            </div>
            <WebCharCounter value={bulletInput} max={MAX_CV_DESCRIPTION} />
          </div>
        </div>
      </WebFormDialog>
    </div>
  );
}
